import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import CourseCard from '../../components/courses/CourseCard';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  fetchCoursePreviews,
  fetchCourseTypes,
} from '../../store/coursesSlice';
import {
  coursesIsLoadingSelector,
  courseTypesSelector,
} from '../../store/selectors';
import Message from '../../components/translation/Message';
import { getCourseTypeName } from '../../utils';
import PageOrSectionHeader from '../../components/typography/PageOrSectionHeader';
import { CoursePreview } from '../../types';
import { WhitelabelContext } from '../../whitelabel-config/WhitelabelProvider';
import { getStaticContentImage } from '../../api/instance-api';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    recommendedCourseList: {
      display: 'flex',
      overflow: 'scroll',
      '& > * + *': {
        marginLeft: theme.spacing(2),
      },
    },
    allCoursesList: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 0fr))',
      gridGap: '24px',
    },
  })
);

const AllCoursesPage = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector(coursesIsLoadingSelector);
  const courseTypes = useSelector(courseTypesSelector);
  const classes = useStyles();
  const location = useLocation();

  const path = getCourseTypeName(location.pathname);
  let courseType =
    courseTypes?.find((type) => type.courseType.name === path) || null;

  const { getImage } = useContext(WhitelabelContext);
  const defaultImg = getImage('defaultMapImage');
  const [defaultImgUrl, setDefaultImgUrl] = useState('');

  useEffect(() => {

    if(!defaultImgUrl) {
      const fetchImage = async (
        imageUrl: string,
        setImage: React.Dispatch<React.SetStateAction<string>>
      ) => {
        const logoData = await getStaticContentImage(imageUrl);
        const imageSrcUrl = window.URL.createObjectURL(logoData);
        setImage(imageSrcUrl);
      };
      const fetchOrSetImage = (
        imageIdOrUrl: string,
        setImage: React.Dispatch<React.SetStateAction<string>>
      ) => {
        if (imageIdOrUrl && imageIdOrUrl.length > 0) {
          fetchImage(imageIdOrUrl, setImage);
        }
      };

      fetchOrSetImage(defaultImg, setDefaultImgUrl);
    }

  }, [defaultImg]);
  
  useEffect(() => {
    if (path && !courseTypes) {
      dispatch(fetchCourseTypes());
    }
  }, [courseTypes, dispatch, path]);

  useEffect(() => {
    if (path && courseType && !courseType.hasBeenFetched) {
      dispatch(fetchCoursePreviews(courseType.courseType.id));
    }
  }, [courseType, courseType?.courseType.id, dispatch, path]);

  const hasCourseTypePreviews = () => {
    return (
      courseType &&
      courseType?.coursePreview?.filter(
        (preview: CoursePreview) => preview.isActive
      )?.length > 0
    );
  };

  const isActive = courseType && courseType.courseType.isActive;

  const isLoadingOrFetching = () => {
    return (
      (courseType && (courseType.isLoading || !courseType.hasBeenFetched)) ||
      isLoading
    );
  };

  const showCourseCard = (coursePreview : CoursePreview) => {
    if(coursePreview.previewImageUrl !== ''){
      return (
        <CourseCard
          coursePreview={coursePreview}
          key={coursePreview.id}
        />
      )
    } else {
      return (
        <CourseCard
          coursePreview={coursePreview}
          key={coursePreview.id}
          defaultImage={defaultImgUrl}
        />
      )
    }
  }
  
  return (
    <Box p={{ xs: 1, md: 4 }}>
      <PageOrSectionHeader>
        <Message id="course.allCoursesPage.browse" />
      </PageOrSectionHeader>
      {isActive && isLoadingOrFetching() && <CircularProgress size={64} />}

      {!isActive && !isLoadingOrFetching() && !hasCourseTypePreviews() && (
        <NoActiveCoursesInType courseType={courseType?.courseType.name} />
      )}

      {isActive && !isLoadingOrFetching() && hasCourseTypePreviews() && (
        <Box className={classes.allCoursesList}>
          {courseType &&
            courseType.coursePreview.map(
              (coursePreview) =>
                coursePreview.isActive && (
                  showCourseCard(coursePreview)
                )
            )}
        </Box>
      )}
    </Box>
  );
};

export default AllCoursesPage;

type NoActiveCoursesInTypeProps = {
  courseType?: string;
};

const NoActiveCoursesInType = ({ courseType }: NoActiveCoursesInTypeProps) => {
  return (
    <Box mt={4}>
      <Typography>
        <Message id="course.allCoursesPage.noCoursesInType" />
        {courseType && courseType}
        {!courseType && <Message id="nav.courses" />}.
      </Typography>
    </Box>
  );
};
